import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA } from '@angular/material/dialog'
import {
  InfoboothDialogFunctionEnum,
  INFOBOOTHS_LOADER,
  InfoboothsEntity,
  InfoboothsFacade,
} from 'libs/gb-dashboard/setup/data-access-infobooths/src'
import * as InfoboothsActions from 'libs/gb-dashboard/setup/data-access-infobooths/src/lib/+state/infobooths.actions'
import { PendingChangesService } from 'libs/gb-dashboard/shared/utils/src/lib/gb-pending-changes/gb-pending-changes.service'
import { take } from 'rxjs'

@Component({
  selector: 'gb-infobooth-edit',
  templateUrl: './gb-infobooth-edit.component.html',
  styleUrls: ['./gb-infobooth-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class GbInfoboothEditComponent implements OnDestroy, OnInit {
  selectedInfobooth$ = this.infoboothsFacade.selectedInfobooths$
  editCreateLoaded$ = this.infoboothsFacade.editCreateLoaded$

  editInfoboothFormFields = {
    name: new FormControl('', [Validators.required]),
    content: new FormControl('', [Validators.required]),
  }

  editInfoboothForm: FormGroup = new FormGroup(this.editInfoboothFormFields)

  get name() {
    return this.editInfoboothForm.get('name')
  }

  get content() {
    return this.editInfoboothForm.get('content')
  }

  dialogData: {
    title?: string
    submitLabel?: string
    submitFunction?: string
  }

  constructor(
    @Inject(MAT_DIALOG_DATA)
    data: {
      title?: string
      submitLabel?: string
    },
    private infoboothsFacade: InfoboothsFacade,
    private pendingChangesService: PendingChangesService,
  ) {
    this.dialogData = data
  }

  ngOnInit(): void {
    this.setupForm()
    this.pendingChangesService.registerPendingChanges(this.editInfoboothForm, this.selectedInfobooth$)
  }

  ngOnDestroy(): void {
    this.pendingChangesService.resetPendingChanges()
    this.infoboothsFacade.dispatch(InfoboothsActions.clearSelectedInfobooth())
  }

  closeDialog() {
    this.pendingChangesService.closeDialogPendingChanges(GbInfoboothEditComponent)
  }

  setupForm() {
    this.selectedInfobooth$.pipe(take(1)).subscribe((infobooth) => {
      this.editInfoboothForm.patchValue({ name: infobooth?.name })
      this.editInfoboothForm.patchValue({ content: infobooth?.content })
    })
  }

  submitForm() {
    this.editInfoboothForm.valid ? this.submitData() : this.onError()
  }

  onError() {
    this.editInfoboothForm.markAllAsTouched()
  }

  submitData() {
    if (this.dialogData.submitFunction == InfoboothDialogFunctionEnum.Update) return this.updateSelectedInfobooth()
    if (this.dialogData.submitFunction == InfoboothDialogFunctionEnum.Create) return this.createInfobooth()
  }

  updateSelectedInfobooth() {
    this.infoboothsFacade.dispatch(
      InfoboothsActions.updateSelectedInfobooth({
        infobooth: this.getInfoboothPayload(),
      }),
    )
  }

  getInfoboothPayload(): Partial<InfoboothsEntity> {
    // quill editor replaces all spaces with '&nbsp', so we need to manually replace them with spaces

    const infobooth: Partial<InfoboothsEntity> = {
      name: this.name?.value,
      content: this.processQuillText(this.content?.value),
    }

    return infobooth
  }

  createInfobooth() {
    this.infoboothsFacade.dispatch(
      InfoboothsActions.createInfobooth({
        infobooth: this.getInfoboothPayload(),
      }),
    )
  }

  processQuillText(quillText: string): string {
    return quillText
      ?.replace(/&nbsp;/g, ' ') // Replace non-breaking spaces with spaces
      .replace(/<p([^>]*)>\s*<\/p>/g, '<p$1><br></p>') // Add <br> inside empty <p> tags
  }

  protected readonly INFOBOOTHS_LOADER = INFOBOOTHS_LOADER
}
